/* eslint-disable no-unused-vars */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';


const SecurityContext = createContext();

export const useSecurity = () => {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error('useSecurity must be used within a SecurityProvider');
  }
  return context;
};

export const SecurityProvider = ({ children }) => {
  const { user, token } = useAuth();
  
  const [securityLevel, setSecurityLevel] = useState('standard');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lastSecurityCheck, setLastSecurityCheck] = useState(Date.now());
  const [securityAlerts, setSecurityAlerts] = useState([]);
  const [auditLog, setAuditLog] = useState([]);

  // Security levels based on user role and permissions
  useEffect(() => {
    if (!user) {
      setSecurityLevel('guest');
      return;
    }

    if (user.role === 'admin') {
      setSecurityLevel('high');
    } else if (['sub-admin', 'accountant'].includes(user.role)) {
      setSecurityLevel('medium');
    } else {
      setSecurityLevel('standard');
    }
  }, [user]);

  // Log security events
  const logSecurityEvent = (event, details = {}) => {
    const logEntry = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      userId: user?.id || 'anonymous',
      userRole: user?.role || 'guest',
      event,
      details,
      ipAddress: 'client-side', // In real app, get from server
      userAgent: navigator.userAgent,
      securityLevel
    };

    setAuditLog(prev => [logEntry, ...prev.slice(0, 99)]); // Keep last 100 entries

    // Send to server in production
    if (process.env.NODE_ENV === 'production' && token) {
      // api.post('/api/security/audit-log', logEntry);
    }
  };

  // Track failed attempts
  const trackFailedAttempt = (action, details = {}) => {
    setFailedAttempts(prev => prev + 1);
    
    logSecurityEvent('failed_attempt', {
      action,
      attemptNumber: failedAttempts + 1,
      ...details
    });

    // Lock account after 5 failed attempts (in real app, implement server-side)
    if (failedAttempts >= 4) {
      logSecurityEvent('account_locked', {
        action,
        reason: 'too_many_failed_attempts'
      });
      
      // In real app, call server to lock account
      // api.post('/api/security/lock-account', { userId: user?.id });
    }
  };

  // Reset failed attempts
  const resetFailedAttempts = () => {
    setFailedAttempts(0);
    logSecurityEvent('failed_attempts_reset');
  };

  // Add security alert
  const addSecurityAlert = (alert) => {
    const newAlert = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      ...alert
    };
    
    setSecurityAlerts(prev => [newAlert, ...prev.slice(0, 9)]); // Keep last 10 alerts
    logSecurityEvent('security_alert', alert);
  };

  // Check security policy compliance
  const checkSecurityPolicy = (action, resource) => {
    const policies = {
      user_management: ['admin', 'sub-admin'],
      event_management: ['admin', 'sub-admin'],
      donation_management: ['admin', 'sub-admin', 'accountant'],
      analytics_access: ['admin', 'sub-admin'],
      settings_access: ['admin', 'sub-admin'],
      gallery_management: ['admin', 'sub-admin'],
      notification_management: ['admin', 'sub-admin'],
      prayer_management: ['admin', 'sub-admin'],
      booking_management: ['admin', 'sub-admin']
    };

    const requiredRoles = policies[resource] || [];
    const hasAccess = requiredRoles.includes(user?.role);

    if (!hasAccess) {
      logSecurityEvent('policy_violation', {
        action,
        resource,
        requiredRoles,
        userRole: user?.role
      });
    }

    return hasAccess;
  };

  // Get security recommendations
  const getSecurityRecommendations = () => {
    const recommendations = [];

    if (securityLevel === 'guest') {
      recommendations.push({
        type: 'warning',
        message: 'Please log in to access secure features',
        priority: 'high'
      });
    }

    if (failedAttempts > 0) {
      recommendations.push({
        type: 'error',
        message: `Multiple failed attempts detected (${failedAttempts})`,
        priority: 'high'
      });
    }

    if (user && !user.emailVerified) {
      recommendations.push({
        type: 'warning',
        message: 'Please verify your email address',
        priority: 'medium'
      });
    }

    if (user && !user.phoneVerified) {
      recommendations.push({
        type: 'info',
        message: 'Consider verifying your phone number for additional security',
        priority: 'low'
      });
    }

    return recommendations;
  };

  // Security health check
  const performSecurityHealthCheck = () => {
    const health = {
      overall: 'good',
      issues: [],
      recommendations: getSecurityRecommendations()
    };

    if (failedAttempts > 0) {
      health.overall = 'warning';
      health.issues.push(`Failed attempts: ${failedAttempts}`);
    }

    if (!user) {
      health.overall = 'info';
      health.issues.push('User not authenticated');
    }

    if (health.issues.length > 0) {
      health.overall = 'warning';
    }

    return health;
  };

  // Clear audit log
  const clearAuditLog = () => {
    setAuditLog([]);
    logSecurityEvent('audit_log_cleared');
  };

  // Clear security alerts
  const clearSecurityAlerts = () => {
    setSecurityAlerts([]);
    logSecurityEvent('security_alerts_cleared');
  };

  const value = {
    // State
    securityLevel,
    failedAttempts,
    lastSecurityCheck,
    securityAlerts,
    auditLog,
    
    // Actions
    logSecurityEvent,
    trackFailedAttempt,
    resetFailedAttempts,
    addSecurityAlert,
    checkSecurityPolicy,
    getSecurityRecommendations,
    performSecurityHealthCheck,
    clearAuditLog,
    clearSecurityAlerts,
    
    // Computed values
    isSecure: securityLevel !== 'guest',
    hasHighSecurity: securityLevel === 'high',
    hasMediumSecurity: ['high', 'medium'].includes(securityLevel),
    
    // Security status
    securityStatus: performSecurityHealthCheck()
  };

  return (
    <SecurityContext.Provider value={value}>
      {children}
    </SecurityContext.Provider>
  );
};
